import {messages} from '@smartmixin/i18n';
import React from 'react';
import {
  DateTimeConfig,
  formatDate,
  formatDuration,
  getScaleStart,
  hourFormatter,
  DAY,
} from '../Dates';
import {formatBearingSpeech} from '../Units';
import {MoonRegime, getIllumination, getInfo, getPosition} from './astronomy';
import {getPhaseMessage} from './phaseName';
import {
  MoonIntl,
  formatIlluminationLine,
  formatMoonApparentSize,
  formatMoonCompass,
  formatMoonDegrees,
  formatMoonDistance,
} from './format';

/**
 * The moon, for one place, right now — reduced to the strings and decisions a
 * card renders.
 *
 * A headless presenter, the moon half of what `Sun.useSunDayView` does. The
 * mobile card (`apps/mobile/.../Moon/MoonInfo/index.tsx`) and the webapp one
 * (`apps/webapp/.../ephemeride/MoonEphemeride/index.tsx`) were hand
 * transcriptions of one another: !483 carried the mobile implementation across
 * by hand, and the regime guard was then fixed on one side only, leaving mobile
 * captioning a moon 41° above the horizon "below the horizon all day" for
 * months (FE #171). This is the seam that makes that class of drift impossible
 * (FE #170, step 5).
 *
 * What deliberately does NOT cross the seam:
 *
 * - **Icon names.** Mobile's rail cells carry Phosphor names; the webapp's carry
 *   none. Each app maps `id` to its own icon, or to nothing.
 * - **testIDs, styles, the accessible-name composition.** The cells hand back
 *   `label`, `value`, `detail` and `speech` as separate fields precisely so each
 *   app can compose its own accessible name — and the two genuinely differ on
 *   the culmination cell, where mobile has none and the webapp falls back to the
 *   altitude.
 * - **Colours.** The observing line's tint is a theme token in each app.
 *
 * `rail.cells` is a fixed triple rather than named fields because both apps
 * render it positionally, so its order is the on-screen order — pinned by the
 * tests for that reason.
 *
 * A hook under `utils/` rather than in `packages/core/src/hooks/`, deliberately
 * and like `Sun.useSunDayView`: it belongs to the `Moon` namespace its callers
 * already import, and the hooks barrel reaches the store and the API client,
 * which is exactly the weight this module is arranged to avoid.
 */

const SECONDS_PER_DAY = 86400;

/** A message descriptor as `@smartmixin/i18n` defines them. */
type Message = typeof messages.astral.nextRise;

export type MoonNowViewParams = {
  latitude: number;
  longitude: number;
  timezone: string;
  /** The instant the card is describing; both apps tick this once a minute. */
  now: number;
  intl: MoonIntl;
  dateTimeConfig: DateTimeConfig;
};

export type MoonCellId = 'rise' | 'transit' | 'set';

export type MoonCell = {
  id: MoonCellId;
  /** "Moonrise". */
  label: string;
  /**
   * "21:53", or `null` on a partial pass — `regime: 'normal'` with only one
   * horizon crossing in the window. The cell degrades on its own rather than
   * collapsing the whole rail.
   */
  value: string | null;
  /** "NE 47" for rise and set, "41°" for the culmination, "" when unknown. */
  detail: string;
  /**
   * The spoken form of `detail` where the printed one is an abbreviation:
   * "East-northeast" for "ENE". `null` on the culmination cell, whose detail is
   * a number and needs no spelling out — the two apps then diverge on purpose,
   * mobile leaving the cell unlabelled and the webapp reading the altitude, and
   * that divergence stays in the apps rather than being frozen in here.
   */
  speech: string | null;
};

/**
 * Two shapes, not one with holes in it.
 *
 * The rail keys on the regime ALONE, deliberately. The obvious guard —
 * `regime !== 'normal' || rise === null || set === null` — is wrong, because
 * the banner underneath only distinguishes `alwaysUp`, so every other case falls
 * through to "below the horizon all day". A `normal` regime can legitimately
 * carry a null rise or a null set: the pass crosses the horizon once in the
 * window rather than twice. Sweeping real `getInfo` over 2026 turns that up from
 * **62°N** upward — Trondheim's latitude and Reykjavik's, not the high Arctic —
 * and at 63.0°N on 6 Sep 2026 10:00 UTC the moon is 41.4° ABOVE the horizon with
 * `rise === null` (FE #171).
 */
export type MoonRail =
  | {kind: 'events'; cells: [MoonCell, MoonCell, MoonCell]}
  | {
      kind: 'noEvents';
      /** "Above the horizon all day" / "Below the horizon all day". */
      label: string;
      /**
       * "Highest point 03:12 · 41°" under `alwaysUp`, `null` under
       * `alwaysDown`. Core used to compute the transit whenever its scan found
       * a rise/set pair regardless of regime, so 347 `alwaysDown` instants in
       * 2026 carried one from a pass two days out — "Below the horizon all
       * day · Highest point 11:40 · 1°" (FE #173). It no longer can.
       */
      transit: string | null;
    };

export type MoonNowView = {
  /**
   * Neither card reads this today — both branch on `rail.kind` — but it is not
   * derivable from the rest: `rail.kind === 'noEvents'` cannot tell `alwaysUp`
   * from `alwaysDown` once the label is a formatted string. It is what the
   * tests assert the regime decision on, and what #169's detail page will need.
   */
  regime: MoonRegime;
  /** Everything the disc needs; the SVG element tree stays in each app. */
  glyph: {fraction: number; waxing: boolean; tilt: number};
  /** "Waning gibbous". */
  phaseName: string;
  /** "94.2% lit · day 16.2 of 29.5". */
  illuminationLine: string;
  /**
   * "364 708 km · 32.75′ across" — the hero's dim third line, settled in pass
   * 2 and shipped as two lines (FE #134). Keyed on `now` like the footer, not
   * on midday like the illumination: distance is where the moon is, not a
   * property of the day.
   */
  distanceLine: string;
  rail: MoonRail;
  /**
   * "Up for 8h43 · now 41° at azimuth 183°", or the below-the-horizon form:
   * "Below the horizon since 16:32 · next rise 23:49", each half dated with
   * a weekday when it falls outside the local day, and each half omitted
   * when the scan found nothing to print. Empty only when the moon is down
   * and neither its last set nor its next rise is within four days.
   */
  footer: string;
  /**
   * "Dark sky 01:12-05:44 (4h32)", or the reason there is none: "Bright all
   * night" when the moon is up through every dark hour, "Twilight all night"
   * when the sun never gets far enough below the horizon for the moon to
   * matter. The card said the first for both until FE #156, on nights the
   * moon never rose.
   */
  observing: string;
};

export const useMoonNowView = ({
  latitude,
  longitude,
  timezone,
  now,
  intl,
  dateTimeConfig,
}: MoonNowViewParams): MoonNowView => {
  const {formatMessage} = intl;

  // Local midday, for the same reason the sun card uses it: local midnight in
  // Paris is 22:00 UTC the day before, and the phase name is evaluated against
  // a 12-hour window either side of the exact instant. Noon is the sample that
  // puts "Full moon" on the day the moon is actually full.
  const midday = getScaleStart(now, timezone, DAY) + SECONDS_PER_DAY / 2;

  /*
   * Illumination at local midday, like the phase name and unlike the pass.
   *
   * The two are different questions. "How lit is it" is a property of the day —
   * the same 12-hour window that puts "Full moon" on the day the moon is full —
   * while rise, culmination and set are relative to the moment you are looking,
   * which is why `info` below is keyed on `now`. Reading the fraction off that
   * `now`-keyed object is what made the card say 94.2% while the detail screen
   * one tap away said 94.8% (FE #135).
   */
  const illumination = React.useMemo(() => getIllumination(midday), [midday]);

  // Keyed on `now`, not on midday. The pass — rise, culmination, set, and the
  // set that has already happened — is relative to the moment you are looking,
  // and a midday-keyed object silently mixes the two: at 21:30 it reported the
  // previous day's set for "below the horizon since", a time 27 minutes in the
  // future for "sets", and no next rise at all.
  //
  // The cost is a culmination scan plus a 289-sample twilight sweep once a
  // minute — roughly 600 suncalc calls, well under a millisecond. Cheap enough
  // not to trade for correctness.
  const info = React.useMemo(
    () => getInfo({timestamp: now, latitude, longitude}),
    [now, latitude, longitude],
  );
  const position = React.useMemo(
    () => getPosition({timestamp: now, latitude, longitude}),
    [now, latitude, longitude],
  );

  const hourFormat = hourFormatter(dateTimeConfig);
  const at = (timestamp: number | null) =>
    timestamp === null ? null : formatDate(timestamp, timezone, hourFormat);

  // Both shared with the detail page rather than written twice — see
  // `format.ts` for why the decimal separator and the compass spacing are
  // decisions this card cannot be allowed to make on its own.
  const degrees = (value: number) => formatMoonDegrees(value, intl);
  const compass = (bearing: number | null) => formatMoonCompass(bearing, intl);

  const rail = ((): MoonRail => {
    if (info.regime !== 'normal') {
      return {
        kind: 'noEvents',
        label: formatMessage(
          info.regime === 'alwaysUp'
            ? messages.astral.aboveHorizonAllDay
            : messages.astral.belowHorizonAllDay,
        ),
        transit:
          info.transit === null
            ? null
            : `${formatMessage(messages.astral.highestPoint)} ${at(
                info.transit,
              )} · ${degrees(info.transitAltitude as number)}`,
      };
    }

    return {
      kind: 'events',
      cells: [
        {
          id: 'rise',
          label: formatMessage(messages.astral.moonrise),
          value: at(info.rise),
          detail: compass(info.riseAzimuth),
          speech: formatBearingSpeech(info.riseAzimuth, intl) ?? '',
        },
        {
          id: 'transit',
          label: formatMessage(messages.astral.highestPoint),
          value: at(info.transit),
          // `transitAltitude` is null exactly when `transit` is — `getInfo`
          // assigns the triple together in both branches, and a 17,760-sample
          // sweep finds no violation — so the detail line empties with the
          // value.
          //
          // Not a cosmetic guard: `intl.formatNumber(null)` returns **"0"**,
          // not "NaN", because `Number(null) === 0`. Dropping it prints a
          // confident "Highest point / — / 0°" instead of anything that looks
          // broken.
          detail:
            info.transitAltitude === null ? '' : degrees(info.transitAltitude),
          speech: null,
        },
        {
          id: 'set',
          label: formatMessage(messages.astral.moonset),
          value: at(info.set),
          detail: compass(info.setAzimuth),
          speech: formatBearingSpeech(info.setAzimuth, intl) ?? '',
        },
      ],
    };
  })();

  /**
   * "Below the horizon since 16:32" when the set was today; "... since Mon
   * 17:31" when it was not. `at()` prints a wall-clock time with no date, and
   * at 68°N on 29 Jun 2026 the next rise was 69.7 hours out: "next rise
   * 01:44" read as two hours away (FE #157). The qualifier is the localised
   * weekday, conditional on the event falling outside the local day, so the
   * footer stays glanceable on the ordinary night it is usually about.
   */
  const localDay = (timestamp: number) =>
    formatDate(timestamp, timezone, 'YYYY-MM-DD');
  const today = localDay(now);
  const when = (timestamp: number, sameDay: Message, otherDay: Message) =>
    localDay(timestamp) === today
      ? formatMessage(sameDay, {time: at(timestamp)})
      : formatMessage(otherDay, {
          day: formatDate(timestamp, timezone, 'ddd'),
          time: at(timestamp),
        });

  const footer = (() => {
    // `info.isUp`, not `position.altitude < 0`: the crossings put the horizon
    // at +0.133°, suncalc's own line, and in the sliver between the two a
    // footer keyed on the altitude says "up for 12h" about the pass the rail
    // says has not started.
    if (!info.isUp) {
      // "Up for 8h43 · now -0.4° above the SW" is three wrong things in one
      // line.
      //
      // `lastSet`, not `set`: while the moon is down the pass on show is the
      // next one, so `set` is a time that has not happened yet — "below the
      // horizon since 21:53" at 21:30. And `nextRise`, not `rise`: under
      // `alwaysDown` the pass is empty but the moon still rises on Thursday,
      // and that is the one thing worth printing.
      return [
        info.lastSet === null
          ? null
          : when(
              info.lastSet,
              messages.astral.belowHorizonSince,
              messages.astral.belowHorizonSinceOn,
            ),
        info.nextRise === null
          ? null
          : when(
              info.nextRise,
              messages.astral.nextRise,
              messages.astral.nextRiseOn,
            ),
      ]
        .filter((part) => part !== null)
        .join(' · ');
    }

    // Gated on the regime as well as the pair, the way the detail page's
    // footer is. Core no longer hands a pass to a polar regime, so the regime
    // term is belt and braces — but "Above the horizon all day" over "Up for
    // 22h49" is the exact card FE #173 is about, and the guard that would
    // have stopped it existed one file over.
    const upFor =
      info.regime === 'normal' && info.rise !== null && info.set !== null
        ? `${formatMessage(messages.astral.upFor, {
            duration: formatDuration(info.set - info.rise, intl),
          })} · `
        : '';

    return `${upFor}${formatMessage(messages.astral.nowAtBearing, {
      altitude: degrees(position.altitude),
      // Bare degrees, deliberately: the message reads "azimuth {direction}", so
      // a compass point here would say "azimuth E 99°".
      direction: degrees(position.azimuth),
    })}`;
  })();

  const {darkWindow} = info;

  return {
    regime: info.regime,
    glyph: {
      fraction: illumination.fraction,
      waxing: illumination.waxing,
      tilt: position.tilt,
    },
    phaseName: formatMessage(getPhaseMessage(illumination.phase)),
    illuminationLine: formatIlluminationLine(illumination, intl),
    distanceLine: `${formatMoonDistance(position.distance, intl)} · ${formatMessage(
      messages.astral.apparentSizeAcross,
      {size: formatMoonApparentSize(position.apparentDiameter, intl)},
    )}`,
    rail,
    footer,
    // Two reasons for no window, two strings. "Bright all night — never fully
    // dark" blames the moon, and at Tromsø on 15 Sep 2026 the moon never
    // rises: it is the sun, at −17.4°, keeping the sky lit (FE #156).
    observing:
      darkWindow !== null
        ? formatMessage(messages.astral.darkSkyWindow, {
            from: at(darkWindow.from) as string,
            to: at(darkWindow.to) as string,
            duration: formatDuration(darkWindow.to - darkWindow.from, intl),
          })
        : formatMessage(
            info.darkWindowBlockedBy === 'twilight'
              ? messages.astral.twilightAllNight
              : messages.astral.brightAllNight,
          ),
  };
};
